"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Wrench, TriangleAlert, VolumeX, MoveDownRight, CircleCheck, ShieldCheck, Clock, ArrowRight } from "lucide-react";
const commonIssues = [{
  title: "Officially IATA Accredited Agency",
  desc: "We maintain the highest global standards in travel management and financial security for our clients.",
  icon: ShieldCheck
}, {
  title: "Global Network of Preferred Partners",
  desc: "Exclusive access to the world's most prestigious hotels, private aviation, and hidden gems.",
  icon: CircleCheck
}, {
  title: "Personalized 1-on-1 Consultation",
  desc: "Dedicated travel designers who understand your unique preferences and craft every detail accordingly.",
  icon: Clock
}];
const processSteps = [{
  value: "step-1",
  title: "1. Inquire",
  content: "Share your vision with us. Whether it's a milestone celebration or a spontaneous escape, our journey begins with understanding your aspirations."
}, {
  value: "step-2",
  title: "2. Connect",
  content: "Collaborate with your dedicated travel designer to refine a bespoke itinerary that aligns perfectly with your lifestyle and expectations."
}, {
  value: "step-3",
  title: "3. Experience",
  content: "Travel with absolute peace of mind. From departure to return, every logistical detail is managed by our expert team behind the scenes."
}, {
  value: "step-4",
  title: "4. Reflect",
  content: "We follow up to ensure every moment exceeded your expectations, using your feedback to further personalize your future global adventures."
}];
const specifications = ["Bespoke Itineraries", "24/7 Concierge Support", "VIP Airport Services", "Private Transfers", "Exclusive Amenities"];
export default function CircuitxProcessTrustSection() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: issuesRef,
    isVisible: isIssuesVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252488" id="circuitx-process-trust-section" className="bg-secondary/20 py-16 md:py-24 overflow-hidden">
      <div className="page-container">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 text-primary bg-primary/10 border-transparent">
            <Wrench className="w-4 h-4 mr-2" />
            The CircuitX Process
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
            Crafting Your Next <span className="text-primary">Global Journey</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Experience a seamless transition from imagination to exploration. Our refined methodology ensures every luxury travel experience is as effortless as it is extraordinary.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div ref={contentRef} className={`lg:col-span-7 space-y-10 transition-all duration-700 delay-100 ease-out ${isContentVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div>
              <h3 className="font-default text-2xl font-bold text-foreground mb-4">
                Why Choose CircuitX?
              </h3>
              <p className="text-muted-foreground font-default mb-6 leading-relaxed">
                In an era of automated bookings, we believe in the power of human connection and expert curation. Our process is designed to eliminate the complexities of luxury travel, allowing you to focus solely on the experience itself. We leverage deep industry relationships to provide value that goes beyond the itinerary.
              </p>
              
              <div className="flex flex-wrap gap-2 mb-8">
                {specifications.map((spec, i) => <Badge key={i} data-index={i} variant="outline" className="bg-secondary/50 text-foreground border-border rounded-md">
                    <CircleCheck className="w-3 h-3 mr-1 text-blue-900" />
                    {spec}
                  </Badge>)}
              </div>
            </div>

            <Separator className="bg-border" />

            <div>
              <h3 className="font-default text-2xl font-bold text-foreground mb-6">
                Our Methodical Approach
              </h3>
              <Accordion type="single" collapsible className="w-full" defaultValue="step-1">
                {processSteps.map((step, i) => <AccordionItem key={i} data-index={i} value={step.value} className="border-border">
                    <AccordionTrigger className="font-default text-lg text-foreground hover:text-primary transition-colors">
                      {step.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground font-default leading-relaxed">
                      {step.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="w-full sm:w-auto group bg-blue-900 text-white hover:bg-blue-950 rounded-md shadow-md">
                <Link to="/ContactUsPage">
                   Begin Your Consultation
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={issuesRef} className={`lg:col-span-5 space-y-8 transition-all duration-700 delay-200 ease-out ${isIssuesVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="relative rounded-xl overflow-hidden shadow-lg border border-border group">
              <div className="absolute inset-0 bg-foreground/5 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687938/img/luxury_travel_consultation_desk.png" alt="Luxury travel designer planning a bespoke itinerary" className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-700" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687938/img/luxury_travel_consultation_desk.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687938/img/luxury_travel_consultation_desk.png-wvc-sizes" loading="lazy" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 to-transparent p-6 z-20">
                <div className="flex items-center gap-2 text-foreground font-default font-bold">
                  <ShieldCheck className="w-5 h-5 text-blue-900" />
                  <span>Uncompromising Trust & Security</span>
                </div>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-xl p-6 border border-border shadow-sm">
              <h4 className="font-default text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-900" />
                Trust Credentials
              </h4>
              <div className="space-y-4">
                {commonIssues.map((issue, i) => {
                const Icon = issue.icon;
                return <Card key={i} data-index={i} className="bg-card border-border shadow-sm hover:shadow-md transition-shadow rounded-md">
                      <CardHeader className="p-4 pb-2 flex flex-row items-center gap-3 space-y-0">
                        <div className="bg-primary/10 p-2 rounded-md shrink-0">
                          <Icon className="w-5 h-5 text-blue-900" />
                        </div>
                        <CardTitle className="font-default text-base text-foreground">
                          {issue.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <CardDescription className="font-default text-sm text-muted-foreground">
                          {issue.desc}
                        </CardDescription>
                      </CardContent>
                    </Card>;
              })}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}